import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ClientInvoiceService } from '../docs/client-invoice/client-invoice.service';
import { CustomerAccountOperationComponent } from './customer-account-operation.component';
import { CustomerAccountService, CustomerAccountSummary } from './customer-account.service';
import { accountBlockedMessage, accountError } from './customer-account.utils';

@Component({
  selector: 'app-customer-account-return',
  template: ` <section class="ca-panel" *ngIf="summary || error">
    <div class="ca-title">
      <mat-icon>account_balance_wallet</mat-icon><strong>Распределение возврата</strong>
    </div>
    <p class="ca-error" *ngIf="error">{{ error }}</p>
    <ng-container *ngIf="summary as s">
      <p class="ca-note" *ngIf="!s.enabled || !s.reconciled">
        {{
          blockedMessage(s.blocked_reason) ||
            'Баланс организации пока недоступен: нужны включение и сверка остатков.'
        }}
      </p>
      <ng-container *ngIf="s.enabled && s.reconciled">
        <div class="ca-mini-summary" *ngIf="!accepted && returnPreview as p">
          <span
            >Стоимость товара <b>{{ p.goods_amount | number: '1.2-2' }}</b></span
          ><span
            >Уменьшение долга <b>{{ p.debt_reduction | number: '1.2-2' }}</b></span
          ><span
            >Освободится на баланс <b>{{ p.released_credit | number: '1.2-2' }}</b></span
          >
        </div>
        <p class="ca-note">
          {{
            accepted
              ? 'Возврат принят. Освободившиеся средства остаются на балансе клиента.'
              : 'После оформления отказа освободившиеся средства останутся на балансе клиента.'
          }}
        </p>
        <div class="ca-mini-summary">
          <span
            >Доступно <b>{{ s.available_credit | number: '1.2-2' }} грн</b></span
          ><span
            >В обработке <b>{{ s.reserved_credit | number: '1.2-2' }}</b></span
          >
        </div>
        <mat-form-field appearance="outline" class="ca-org" *ngIf="accepted"
          ><mat-label>Распорядиться средствами</mat-label
          ><mat-select [(value)]="choice"
            ><mat-option value="KEEP">Оставить на балансе</mat-option
            ><mat-option value="ALLOCATE">Зачесть в другой заказ</mat-option
            ><mat-option value="PAYOUT">Выдать деньги клиенту</mat-option></mat-select
          ></mat-form-field
        >
        <button
          mat-flat-button
          color="primary"
          *ngIf="accepted && choice !== 'KEEP'"
          [disabled]="s.available_credit <= 0"
          (click)="continue()"
        >
          {{ choice === 'ALLOCATE' ? 'Выбрать заказы и суммы' : 'Указать сумму и способ выплаты' }}
        </button>
        <p class="ca-note" *ngIf="accepted && choice === 'KEEP'">
          Дополнительного действия не требуется. Зачёт в заказ и выплата выполняются только по
          выбору менеджера.
        </p>
        <a
          mat-button
          [routerLink]="['/client/profile', clientId]"
          [queryParams]="{ tab: 'settlements', org_id: orgId }"
          >Все взаиморасчёты клиента</a
        >
      </ng-container>
    </ng-container>
  </section>`,
  styleUrls: ['./customer-account.scss']
})
export class CustomerAccountReturnComponent implements OnChanges, OnDestroy {
  @Input() invoiceId = 0;
  @Input() accepted = true;
  @Input() proposedAmount = 0;
  @Output() managedChange = new EventEmitter<boolean>();
  summary?: CustomerAccountSummary;
  clientId = 0;
  clientName = '';
  orgId = 0;
  orgName = '';
  choice = 'KEEP';
  error = '';
  returnPreview?: {
    goods_amount: number;
    debt_reduction: number;
    released_credit: number;
    available_after: number;
  };
  private previewRevision = 0;
  readonly blockedMessage = accountBlockedMessage;
  private revision = 0;
  private subscription: Subscription;
  constructor(
    private invoices: ClientInvoiceService,
    private accounts: CustomerAccountService,
    private dialog: MatDialog
  ) {
    this.subscription = accounts.changed.subscribe((scope) => {
      if (scope.clientId === this.clientId && scope.orgId === this.orgId) void this.load();
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.proposedAmount && !changes.invoiceId && this.summary) void this.loadPreview();
    else void this.load();
  }
  ngOnDestroy() {
    this.revision++;
    this.subscription.unsubscribe();
  }
  async load() {
    const revision = ++this.revision;
    if (!this.invoiceId) return;
    this.error = '';
    try {
      const invoice = await firstValueFrom(this.invoices.getOne(this.invoiceId));
      if (revision !== this.revision) return;
      this.clientId = invoice.client?.id;
      this.orgId = invoice.org?.id || 0;
      this.clientName = invoice.client?.name || '';
      this.orgName = invoice.org?.name || '';
      if (!this.clientId || !this.orgId) return;
      const summary = await firstValueFrom(this.accounts.summary(this.clientId, this.orgId));
      if (revision !== this.revision) return;
      this.summary = summary;
      this.managedChange.emit(summary.enabled && summary.reconciled);
      await this.loadPreview();
    } catch (error) {
      if (revision === this.revision)
        this.error = accountError(error, 'Не удалось получить состояние клиентского баланса');
    }
  }
  async loadPreview() {
    const revision = ++this.previewRevision;
    this.returnPreview = undefined;
    if (
      this.accepted ||
      this.proposedAmount <= 0 ||
      !this.clientId ||
      !this.orgId ||
      !this.summary?.enabled
    )
      return;
    try {
      const preview = await firstValueFrom(
        this.accounts.previewReturn(this.clientId, this.orgId, this.invoiceId, this.proposedAmount)
      );
      if (revision === this.previewRevision) this.returnPreview = preview;
    } catch (error) {
      if (revision === this.previewRevision)
        this.error = accountError(error, 'Не удалось рассчитать освобождение средств');
    }
  }
  continue() {
    if (
      !this.summary?.enabled ||
      !this.summary.reconciled ||
      !this.accepted ||
      this.choice === 'KEEP'
    )
      return;
    this.dialog.open(CustomerAccountOperationComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      data: {
        clientId: this.clientId,
        clientName: this.clientName,
        orgId: this.orgId,
        orgName: this.orgName,
        kind: this.choice,
        summary: this.summary
      }
    });
  }
}
